import {
	RecurrentQuestionDetailsResponse,
	ArticleFrequency,
} from '@models/RecurrentQuestions'

export interface PDFData {
	context: {
		discipline: string
		jury: string
	}
	stats: {
		totalArticles: number
		totalQuestions: number
		top10: string[]
		articleRange: string
	}
	distribution: {
		veryHigh: { label: string; count: string; range: string }
		high: { label: string; count: string; range: string }
		medium: { label: string; count: string; range: string }
		low: { label: string; count: string; range: string }
	}
	lists: {
		priority: string[]
		important: string[]
		regular: string[]
		top100: string[]
	}
	themes: {
		books: { title: string; subtitle: string; color: string }[]
		topByTheme: { title: string; value: string; color: string }[]
	}
}

const hslToHex = (h: number, s: number, l: number) => {
	l /= 100
	const a = (s * Math.min(l, 1 - l)) / 100
	const f = (n: number) => {
		const k = (n + h / 30) % 12
		const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)

		return Math.round(255 * color)
			.toString(16)
			.padStart(2, '0')
	}

	return `#${f(0)}${f(8)}${f(4)}`
}

const generateDistinctSafeColor = (index: number): string => {
	const goldenAngle = 137.508
	let hue = (index * goldenAngle) % 360

	const isForbidden = (h: number) => {
		return (
			h >= 350 || h <= 45 || (h >= 85 && h <= 165) || (h >= 190 && h <= 245)
		)
	}

	let attempts = 0

	while (isForbidden(hue) && attempts < 20) {
		hue = (hue + 29) % 360
		attempts += 1
	}

	const lightness = 40
	const saturation = 65

	return hslToHex(hue, saturation, lightness)
}

const cleanName = (name: string): string => name.split('(')[0].trim()

const extractArticleRange = (name: string): string =>
	(name.match(/\(Arts.*?\)/) || [''])[0]

const formatArticle = (art: ArticleFrequency): string =>
	`${art.article} (${art.count}x)`

const getArticleNumber = (name: string) => {
	const match = name.match(/\d+/)

	return match ? parseInt(match[0], 10) : 0
}

const sortTop10 = (a: ArticleFrequency, b: ArticleFrequency) => {
	const countDiff = b.count - a.count

	if (countDiff !== 0) return countDiff

	return getArticleNumber(a.article) - getArticleNumber(b.article)
}

export const adaptRecurrentDetailsToPDF = (
	apiData: RecurrentQuestionDetailsResponse,
	disciplineName: string,
	juryName: string
): PDFData => {
	const {
		statistics,
		frequencyDistribution,
		top100Articles,
		subMatterDistribution = [],
	} = apiData

	const top10 = [...statistics.top10Articles]
		.sort(sortTop10)
		.map((art, i) => `${i + 1}. ${formatArticle(art)}`)

	const top100 = top100Articles.map(formatArticle)

	const getList = (articles: ArticleFrequency[]) => articles.map(formatArticle)

	const priority = getList(frequencyDistribution.veryHigh.articles)
	const important = getList(frequencyDistribution.high.articles)
	const regular = getList(frequencyDistribution.medium.articles)

	const articleRange = `(${statistics.firstArticle} ao ${statistics.lastArticle})`

	const matterColorMap = new Map<string, { color: string; subtitle: string }>()

	const usedColors = new Set<string>()
	let colorIndex = 0

	subMatterDistribution.forEach((item) => {
		const parentName = cleanName(item.matterName)

		if (!matterColorMap.has(parentName)) {
			let color = generateDistinctSafeColor(colorIndex)

			while (usedColors.has(color)) {
				colorIndex += 1
				color = generateDistinctSafeColor(colorIndex)
			}

			usedColors.add(color)

			matterColorMap.set(parentName, {
				color,
				subtitle: extractArticleRange(item.matterName),
			})
			colorIndex += 1
		}
	})

	const books = Array.from(matterColorMap.entries()).map(([title, data]) => ({
		title,
		subtitle: data.subtitle,
		color: data.color,
	}))

	const topByTheme = subMatterDistribution
		.filter((item) => item.topArticle)
		.map((item) => {
			const parentName = cleanName(item.matterName)
			const subName = cleanName(item.subMatterName)

			const parentStyle = matterColorMap.get(parentName)

			const articleValue = item.topArticle ? formatArticle(item.topArticle) : ''

			return {
				title: subName,
				value: articleValue,
				color: parentStyle?.color || '#cbd5e1',
			}
		})

	return {
		context: {
			discipline: disciplineName,
			jury: juryName,
		},
		stats: {
			totalArticles: statistics.totalUniqueArticles,
			totalQuestions: statistics.totalQuestionsAnalyzed,
			top10,
			articleRange,
		},
		distribution: {
			veryHigh: {
				label: `Muito Alta (${frequencyDistribution.veryHigh.range})`,
				count: `${frequencyDistribution.veryHigh.count}`,
				range: frequencyDistribution.veryHigh.range,
			},
			high: {
				label: `Alta (${frequencyDistribution.high.range})`,
				count: `${frequencyDistribution.high.count}`,
				range: frequencyDistribution.high.range,
			},
			medium: {
				label: `Média (${frequencyDistribution.medium.range})`,
				count: `${frequencyDistribution.medium.count}`,
				range: frequencyDistribution.medium.range,
			},
			low: {
				label: `Baixa (${frequencyDistribution.low.range})`,
				count: `${frequencyDistribution.low.count}`,
				range: frequencyDistribution.low.range,
			},
		},
		lists: {
			priority,
			important,
			regular,
			top100,
		},
		themes: {
			books,
			topByTheme,
		},
	}
}
